import type { Permissions } from '../contexts/session-context';

export interface Link {
  name: string;
  url: string;
  value: string;
  chip?: string;
}

export interface NavLinks {
  platform?: Link[];
  setup?: Link[];
  appSetup?: Link[];
  organization?: Link[];
  settings?: Link[];
}

const links = (): NavLinks => ({
  platform: [
    { name: 'Dashboard', url: '/home', value: 'hasIssuesInstalled' },
    { name: 'Issues', url: '/issues', value: 'hasIssuesInstalled' },
    { name: 'Tasks', url: '/tasks', value: 'hasTasksInstalled' },
    { name: 'Apps', url: '/apps', value: 'standard' },
    {
      name: 'Insights', url: '/insights', value: 'isMultiAppEnabled', chip: 'Beta',
    },
  ],
  setup: [
    { name: 'Imports', url: '/imports', value: 'canImportRecords' },
    { name: 'Exports', url: '/exports', value: 'canExportRecords' },
    { name: 'Projects', url: '/projects', value: 'canManageProjects' },
  ],
  appSetup: [
    { name: 'Map Layers', url: '/layers', value: 'canManageLayers' },
    { name: 'Choice Lists', url: '/choice_lists', value: 'canManageChoiceLists' },
    { name: 'Classification Sets', url: '/classification_sets', value: 'canManageClassificationSets' },
  ],
  organization: [
    { name: 'Member Profile', url: '/settings/profile', value: 'standard' },
    { name: 'Organization Profile', url: '/settings/organization', value: 'canUpdateOrganization' },
    { name: 'Billing', url: '/settings/subscription', value: 'canManageSubscription' },
    { name: 'Groups', url: '/groups', value: 'canManageGroups' },
    { name: 'Members', url: '/settings/members', value: 'canManageMembers' },
    { name: 'Roles', url: '/settings/roles', value: 'canManageRoles' },
    { name: 'API', url: '/settings/api', value: 'canManageApi' },
    { name: 'Webhooks', url: '/settings/webhooks', value: 'canManageWebhooks' },
    { name: 'Audit Logs', url: '/settings/audit_logs', value: 'canManageAuditLogs' },
  ],
});

function filtered(all: NavLinks, section: string, permissions: Record<string, boolean>) {
  return all[section].filter((link: Link) => link.value === 'standard' || permissions[link.value]);
}

function setFilteredLinks(permissions: Permissions, isMultiAppEnabled: boolean) {
  const allLinks = links();

  const allPermissions: Record<string, boolean> = {
    ...permissions,
    isMultiAppEnabled,
  };

  return {
    platform: filtered(allLinks, 'platform', allPermissions),
    setup: permissions.orgInGoodStanding ? filtered(allLinks, 'setup', allPermissions) : [],
    appSetup: permissions.orgInGoodStanding ? filtered(allLinks, 'appSetup', allPermissions) : [],
    organization: permissions.orgInGoodStanding ? filtered(allLinks, 'organization', allPermissions) : [],
  };
}

export default setFilteredLinks;
